import { Button, Grid, Image, Modal } from "antd";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { BackButton, CustomMessage, LoginForm, OTPForm } from "../components";
import { H4, P1 } from "../components/NormalFonts";
import RegisterForm from "../components/RegisterForm";
import SetPasswordForm from "../components/SetPasswordForm";
import localStorageKey from "../enumerations/localStorageKey";
import { useSignin, useSignup } from "../hooks/Authentication/auth.hooks";
import { signinParamType, signupParamType } from "../hooks/Authentication/data";
import { useGetProfile } from "../hooks/Profile/profile.hooks";
import { pushGtag } from "../plugins/utils";
import { AuthContextType, FormConfigType } from "./data";
import "./style.scss";
import styles from "./styles.module.scss";
import { useLocalStorage } from "./useLocalStorage";

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  setAuthModalVisible: () => {},
  loading: false,
  userInfo: undefined,
  setCurrentFormConfig: () => {},
  setLoginForm: () => {},
  setRegisterForm: () => {},
});

type AuthContextProviderType = PropsWithChildren & {};

export type FirstLoginData = {
  account: string;
  token: string;
};

export const AuthContextProvider = ({ children }: AuthContextProviderType) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [authModalVisible, setAuthModalVisible] = useState<boolean>(false);
  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();

  const { mutate: login } = useSignin();
  const { mutate: register } = useSignup();

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>("");
  const [forgotPasswordOTP, setForgotPasswordOTP] = useState<string>("");

  const [openSetProfileModal, setOpenSetProfileModal] =
    useState<boolean>(false);

  const [token, setToken] = useLocalStorage(localStorageKey.TOKEN, "");
  const [userInfo, setUserInfo] = useLocalStorage(
    localStorageKey.USER_INFO,
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);

  const { data: profile } = useGetProfile({
    enabled:
      !!token &&
      (!userInfo || userInfo === "" || window.location.pathname === "/profile"),
  });

  useEffect(() => {
    if (!profile) return;
    setUserInfo(profile);
  }, [profile]);

  const onRegisterFinish = (values: signupParamType) => {
    setLoading(true);
    register(
      { params: values },
      {
        onSuccess: async (data: any) => {
          pushGtag({
            event: "sign_up",
          });

          const token = data.data.data.token;
          setToken(token);
          setIsAuthenticated(true);
          setLoading(false);
          setAuthModalVisible(false);
          if (window.location.pathname !== "/search/") {
            setTimeout(() => {
              setOpenSetProfileModal(true);
            }, 1000);
          }
        },
        onError: (error: any) => {
          console.debug("error", error);
          CustomMessage({
            alertMessage: t([
              `error.${error.data.result.code}`,
              `${error.data.result.message}`,
            ]),
            alertType: "error",
          });
          setLoading(false);
        },
      }
    );
  };

  const onLoginFinish = (values: signinParamType) => {
    setLoading(true);
    login(
      { params: values },
      {
        onSuccess: async (data: any) => {
          pushGtag({
            event: "login",
          });

          const token = data.data.data.token;
          setToken(token);
          setIsAuthenticated(true);
          setLoading(false);
          setAuthModalVisible(false);
          CustomMessage({
            alertType: "success",
            alertMessage: t("normal.login.success"),
          });
        },
        onError: (error: any) => {
          CustomMessage({
            alertMessage: t([
              `error.${error.data.result.code}`,
              error.data.result.message,
            ]),
            alertType: "error",
          });
          setLoading(false);
        },
      }
    );
  };

  const onOTPFinishCallback = (account: string, otp: string) => {
    setForgotPasswordEmail(account);
    setForgotPasswordOTP(otp);
  };

  const onOpenForgetPassword = (e: React.MouseEvent<HTMLElement>) => {
    setCurrentFormConfig(AUTH_FORM_CONFIG.forgot_password);
  };

  const onForgotPasswordFinish = () => {};

  const onBackPressed = (e: React.MouseEvent<HTMLElement>) => {
    setCurrentFormConfig(
      (prevConfig: FormConfigType) => AUTH_FORM_CONFIG[prevConfig.previousForm]
    );
  };

  const setLoginForm = () => {
    setCurrentFormConfig(AUTH_FORM_CONFIG.login);
  };

  const setRegisterForm = () => {
    setCurrentFormConfig(AUTH_FORM_CONFIG.register);
  };

  const AUTH_FORM_CONFIG: Record<string, FormConfigType> = {
    register: {
      id: "register",
      title: <H4 weight={"heavy"}>{t("normal.register")}</H4>,
      form: <RegisterForm onFinish={onRegisterFinish} loading={loading} />,
      showCloseButton: true,
      showBackButton: false,
      previousForm: "",
    },
    login: {
      id: "login",
      title: <H4 weight={"heavy"}>{t("normal.login")}</H4>,
      form: (
        <LoginForm
          onFinish={onLoginFinish}
          onForgetPassword={onOpenForgetPassword}
        />
      ),
      showCloseButton: true,
      showBackButton: false,
      previousForm: "",
    },
    forgot_password: {
      id: "forgot_password",
      title: t("normal.forgotpassword"),
      form: <OTPForm onFinishCallback={onOTPFinishCallback} />,
      showCloseButton: false,
      showBackButton: true,
      previousForm: "login",
    },
    set_password: {
      id: "set_password",
      title: t("auth.title.resetpassword"),
      form: (
        <SetPasswordForm
          account={forgotPasswordEmail}
          otp={forgotPasswordOTP}
          onFinishCallback={onForgotPasswordFinish}
        />
      ),
      showCloseButton: false,
      showBackButton: true,
      previousForm: "forgot_password",
    },
  };

  const [currentFormConfig, setCurrentFormConfig] = useState<FormConfigType>(
    AUTH_FORM_CONFIG.login
  );

  const closeModal = () => {
    setAuthModalVisible(false);
  };

  //Reset forgot password email when form is not in OTP
  useEffect(() => {
    if (
      currentFormConfig?.id !== "set_password" &&
      currentFormConfig?.id !== "forgot_password"
    ) {
      setForgotPasswordEmail("");
      setForgotPasswordOTP("");
    }
  }, [currentFormConfig]);

  useEffect(() => {
    if (forgotPasswordEmail !== "" && forgotPasswordOTP !== "")
      setCurrentFormConfig(AUTH_FORM_CONFIG.set_password);
  }, [forgotPasswordEmail, forgotPasswordOTP]);

  useEffect(() => {
    const userIsLogin = !!token;
    setIsAuthenticated(userIsLogin);
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setAuthModalVisible,
        loading,
        userInfo,
        setCurrentFormConfig,
        setLoginForm,
        setRegisterForm,
      }}
    >
      {children}
      <Modal
        destroyOnClose={true}
        className={`custom-login-modal ${screens.xs ? "xs-style" : ""}`}
        title={<H4 weight={"heavy"}>{currentFormConfig?.title}</H4>}
        open={authModalVisible}
        onCancel={closeModal}
        closable={currentFormConfig?.showCloseButton}
        maskClosable={true}
        footer={false}
        width={{
          xxl: "var(--max-auth-modal-width)",
          xl: "var(--max-auth-modal-width)",
          lg: "var(--max-auth-modal-width)",
          md: "var(--max-auth-modal-width)",
          sm: "var(--max-auth-modal-width)",
          xs: "100vw",
        }}
        centered
      >
        {currentFormConfig?.showBackButton ? (
          <BackButton className="back-button" onClick={onBackPressed} />
        ) : null}
        {currentFormConfig?.form}
      </Modal>

      <Modal
        className={`custom-popup-renew-profile ${screens.xs ? "small" : ""}`}
        open={openSetProfileModal}
        centered
        onCancel={() => {
          setOpenSetProfileModal(false);
        }}
        width={464}
        closable={true}
        footer={false}
      >
        <div>
          <Image
            src={`/images/contents/${process.env.REACT_APP_PROD_NAME_DEV}/home/popup_image.png`}
            preview={false}
            style={{
              width: "100%",
              objectFit: "cover",
              height: "auto",
              borderRadius: 6,
            }}
          />
          <div className={styles.contentContainer}>
            <P1 weight="medium" color="var(--table-text-content4)">
              {t("popup.setprofile.title", {
                name: process.env.REACT_APP_PROD_NAME_DISPLAY,
              })}
            </P1>
            <br />
            <P1 color="var(--table-text-content4)">
              {t("popup.setprofile.content")}
            </P1>
            <Button
              type="primary"
              style={{
                width: 287,
                alignSelf: "center",
                marginTop: 28,
                maxWidth: "90%",
              }}
              onClick={() => {
                setOpenSetProfileModal(false);
                navigate("/profile");
              }}
            >
              {t("normal.go")}
            </Button>
          </div>
        </div>
      </Modal>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const LoadingValue = useContext(AuthContext);
  return LoadingValue;
};
